/**
 * UploadProgressToast - Toast pour afficher la progression des uploads
 * Montre: en cours, succès, erreur avec liens vers les fichiers
 */

import React, { useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  Check,
  AlertCircle,
  X,
  ExternalLink,
  Cloud,
  Loader2
} from 'lucide-react';

const UploadProgressToast = memo(function UploadProgressToast({
  upload,
  onDismiss,
  autoDismissDelay = 5000,
}) {
  useEffect(() => {
    if (!upload) return;

    // Auto-dismiss après succès ou erreur
    if (upload.status === 'success' || upload.status === 'error') {
      const timer = setTimeout(() => {
        if (onDismiss) onDismiss();
      }, autoDismissDelay);
      return () => clearTimeout(timer);
    }
  }, [upload, onDismiss, autoDismissDelay]);

  if (!upload) return null;

  const getStatusConfig = () => {
    switch (upload.status) {
      case 'uploading':
        return {
          icon: Loader2,
          iconClass: 'text-blue-400 animate-spin',
          bgClass: 'from-blue-900/95 to-blue-800/95 border-blue-700/50',
          title: 'Upload en cours...',
          subtitle: upload.fileName || 'Envoi vers Google Drive',
        };
      case 'success':
        return {
          icon: Check,
          iconClass: 'text-green-400',
          bgClass: 'from-green-900/95 to-green-800/95 border-green-700/50',
          title: 'Upload réussi !',
          subtitle: upload.fileName || 'Fichier ajouté à Google Drive',
        };
      case 'error':
        return {
          icon: AlertCircle,
          iconClass: 'text-red-400',
          bgClass: 'from-red-900/95 to-red-800/95 border-red-700/50',
          title: 'Erreur d\'upload',
          subtitle: upload.error || 'Impossible d\'envoyer le fichier',
        };
      default:
        return {
          icon: Cloud,
          iconClass: 'text-zinc-400',
          bgClass: 'from-zinc-900/95 to-zinc-800/95 border-zinc-700/50',
          title: 'Préparation...',
          subtitle: '',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className={`
          fixed bottom-24 left-4 right-4 z-[9999]
          max-w-md mx-auto
          overflow-hidden rounded-xl shadow-2xl
          bg-gradient-to-r ${config.bgClass}
          backdrop-blur-xl
          border
        `}
      >
        {/* Progress bar pour l'upload en cours */}
        {upload.status === 'uploading' && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-black/20 overflow-hidden">
            <motion.div
              className="h-full bg-blue-400"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: 'easeInOut'
              }}
              style={{ width: '50%' }}
            />
          </div>
        )}

        <div className="flex items-center gap-3 p-4">
          {/* Icon */}
          <div className={`
            flex-shrink-0 p-2.5 rounded-xl
            bg-white/10
          `}>
            <Icon className={`w-5 h-5 ${config.iconClass}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">
              {config.title}
            </p>
            <p className="text-xs text-zinc-300 truncate">
              {config.subtitle}
            </p>
            {upload.folderPath && (
              <p className="text-[10px] text-zinc-400 mt-0.5 truncate">
                📁 {upload.folderPath}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Lien vers le fichier */}
            {upload.status === 'success' && upload.webViewLink && (
              <motion.a
                href={upload.webViewLink}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                  bg-green-500 text-white text-sm font-medium
                  hover:bg-green-400 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Voir
              </motion.a>
            )}

            {/* Bouton fermer */}
            {upload.status !== 'uploading' && (
              <button
                onClick={onDismiss}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-200
                  hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
});

export { UploadProgressToast };
export default UploadProgressToast;
